import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import quizQuestions from '../quizQuestions';

// Default user model: start all users with an Elo rating of 100.
const defaultUserModel = {
  preferredTypes: ["Logical Reasoning", "Pattern Recognition"],
  eloRating: 100,
  skillLevel: "Intermediate",
  speedAccuracyBalance: "Neutral"
};

// Helper: Fisher–Yates shuffle.
const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

// Returns 5 session questions based on the current Elo rating bracket.
// However, in this screen, we filter questions solely by the chosen difficulty.
const getSessionQuestionsByDifficulty = (difficulty) => {
  // Filter quizQuestions to only those matching the chosen difficulty.
  const filtered = quizQuestions.filter(q => q.difficulty === difficulty);
  // Shuffle and take 5.
  return shuffleArray(filtered).slice(0, 5);
};

const TimedQuizPersonalizedScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Retrieve username and selected difficulty (e.g., "Easy", "Medium", "Hard") from state.
  const { username, difficulty } = location.state || { username: 'Guest', difficulty: 'Easy' };

  // Retrieve or initialize the user model from localStorage.
  const storedUserModel = localStorage.getItem('userModel');
  const userModel = storedUserModel ? JSON.parse(storedUserModel) : defaultUserModel;

  // Get 5 session questions filtered by the chosen difficulty.
  const [sessionQuestions] = useState(() => getSessionQuestionsByDifficulty(difficulty));

  // Component state variables.
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [timer, setTimer] = useState(30); // Allowed time will be set dynamically.
  const [times, setTimes] = useState([]); // Record time taken for each question.

  // When a new question starts, set allowed time based on the current Elo.
  // Allowed time = 30 * (upperBound / current Elo)
  useEffect(() => {
    if (quizStarted) {
      const currentElo = userModel.eloRating;
      let upperBound = 100;
      if (currentElo <= 100) upperBound = 100;
      else if (currentElo <= 200) upperBound = 200;
      else if (currentElo <= 300) upperBound = 300;
      else if (currentElo <= 400) upperBound = 400;
      else if (currentElo <= 500) upperBound = 500;
      else if (currentElo <= 600) upperBound = 600;
      else if (currentElo <= 700) upperBound = 700;
      else upperBound = 800;
      const allowedTime = 30 * (upperBound / currentElo);
      setTimer(allowedTime);
    }
  }, [currentQuestionIndex, quizStarted, userModel.eloRating]);

  // Timer countdown effect.
  useEffect(() => {
    let interval = null;
    if (quizStarted && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (quizStarted && timer <= 0) {
      clearInterval(interval);
      alert(`Time's up! Your score: ${score}`);
      navigate('/');
    }
    return () => clearInterval(interval);
  }, [quizStarted, timer, score, navigate]);

  // Start the quiz: reset states and record start time.
  const handleStartQuiz = () => {
    setQuizStarted(true);
    setCurrentQuestionIndex(0);
    setScore(0);
    setSelectedAnswer(null);
    setTimes([]);
  };

  // Elo update function.
  // Computes combined performance based on normalized score and normalized time,
  // then updates Elo using: NewElo = CurrentElo + K × (CombinedPerformance − 1)
  const updateEloRating = (timeTaken, isCorrect) => {
    const currentElo = userModel.eloRating;
    const desiredTime = 15; // Ideal time per question in seconds.
    const timeFactor = (desiredTime - timeTaken) / desiredTime;
    const performanceScore = (isCorrect ? 1 : 0) + 0.5 * timeFactor;
    const K = 32;
    const newElo = currentElo + K * (performanceScore - 1);
    const clampedElo = Math.max(0, Math.min(newElo, 800));
    userModel.eloRating = clampedElo;
    localStorage.setItem('userModel', JSON.stringify(userModel));
  };

  // Handle answer selection.
  const handleAnswer = (option) => {
    // Set the selected answer and update score if correct.
    setSelectedAnswer(option);
    const currentQuestion = sessionQuestions[currentQuestionIndex];
    const isCorrect = option === currentQuestion.answer;
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
    // Calculate time taken for this question.
    // We assume the allowed time was set by the effect.
    // For calculation, recalc allowedTime as done in the effect.
    let upperBound = 100;
    if (userModel.eloRating <= 100) upperBound = 100;
    else if (userModel.eloRating <= 200) upperBound = 200;
    else if (userModel.eloRating <= 300) upperBound = 300;
    else if (userModel.eloRating <= 400) upperBound = 400;
    else if (userModel.eloRating <= 500) upperBound = 500;
    else if (userModel.eloRating <= 600) upperBound = 600;
    else if (userModel.eloRating <= 700) upperBound = 700;
    else upperBound = 800;
    const allowedTime = 30 * (upperBound / userModel.eloRating);
    const timeTaken = allowedTime - timer;
    setTimes(prev => [...prev, timeTaken]);
  };

  // Handle next question button click
  const handleNextQuestion = () => {
    if (currentQuestionIndex < sessionQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
    } else {
      // End quiz: compute overall metrics and update Elo.
      const currentQuestion = sessionQuestions[currentQuestionIndex];
      const isCorrect = selectedAnswer === currentQuestion.answer;
      const correctCount = score;
      const scorePercentage = (correctCount / 5) * 100;
      const totalTime = times.reduce((a, b) => a + b, 0);
      const averageTime = totalTime / 5;
      // Normalize score (0-1)
      const normalizedScore = scorePercentage / 100;
      // Expected time for chosen difficulty.
      const expectedTime = difficulty === "Easy" ? 10 : difficulty === "Medium" ? 15 : 20;
      const normalizedTime = expectedTime / averageTime; // >1 if faster
      const combinedPerformance = normalizedScore * normalizedTime;
      updateEloRating(times[times.length - 1], isCorrect);
      const sessionData = {
        username,
        prototype: "Timed Personalized",
        score: scorePercentage,
        averageTime,
        combinedPerformance,
        updatedEloRating: userModel.eloRating
      };
      navigate("/", { state: { sessionData } });
    }
  };

  const currentQuestion = sessionQuestions[currentQuestionIndex];

  return (
    <div style={styles.container}>
      <h2 style={styles.greeting}>
        Hello, {username}! (Timed Personalized - {difficulty})
      </h2>
      {!quizStarted ? (
        <button style={styles.button} onClick={handleStartQuiz}>
          Start Timed Personalized Quiz
        </button>
      ) : (
        <div style={styles.quizContainer}>
          <p style={styles.timer}>Time Left: {Math.floor(timer)}s</p>
          <h3 style={styles.question}>{currentQuestion.question}</h3>
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              style={{
                ...styles.optionButton,
                backgroundColor: selectedAnswer === null 
                  ? "#b0c4de" 
                  : option === currentQuestion.answer 
                    ? "#90ee90" // Light green for correct answer
                    : selectedAnswer === option 
                      ? option === currentQuestion.answer ? "#90ee90" : "#ffcccb" // Red if selected wrong
                      : "#b0c4de",
              }}
              onClick={() => handleAnswer(option)}
              disabled={selectedAnswer !== null}
            >
              {option}
            </button>
          ))}
          {selectedAnswer !== null && (
            <div style={styles.explanation}>
              <p style={styles.explanationTitle}>Explanation:</p>
              <p style={styles.explanationText}>{currentQuestion.explanation}</p>
              <button 
                style={styles.nextButton}
                onClick={handleNextQuestion}
              >
                {currentQuestionIndex < sessionQuestions.length - 1 ? "Next Question" : "Finish Quiz"}
              </button>
            </div>
          )}
          <p style={styles.progress}>
            Question {currentQuestionIndex + 1} of {sessionQuestions.length}
          </p>
          <p style={styles.score}>Score: {score}</p>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: "20px",
    textAlign: "center",
    backgroundColor: "#e6f0fa",
  },
  greeting: {
    color: "#4682b4",
    fontSize: "2rem",
  },
  timer: {
    color: "#ff6347",
    fontSize: "1.5rem",
  },
  quizContainer: {
    marginTop: "20px",
  },
  question: {
    fontSize: "1.5rem",
    color: "#4682b4",
  },
  optionButton: {
    display: "block",
    width: "80%",
    margin: "10px auto",
    padding: "10px",
    fontSize: "1rem",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    backgroundColor: "#b0c4de",
    color: "#333",
    transition: "background-color 0.3s ease", // Smooth transition for feedback.
  },
  progress: {
    marginTop: "20px",
    color: "#4682b4",
  },
  score: {
    fontSize: "1.25rem",
    color: "#4682b4",
    marginTop: "20px",
  },
  button: {
    padding: "10px 20px",
    fontSize: "1.25rem",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    backgroundColor: "#4682b4",
    color: "#fff",
    marginTop: "20px",
  },
  explanation: {
    backgroundColor: "#f0f8ff", // Alice Blue
    borderRadius: "8px",
    padding: "10px 15px",
    margin: "15px auto",
    width: "80%",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    textAlign: "left",
  },
  explanationTitle: {
    fontWeight: "bold",
    color: "#4682b4",
    marginBottom: "5px",
  },
  explanationText: {
    color: "#333",
    lineHeight: "1.4",
  },
  nextButton: {
    backgroundColor: "#4682b4",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    margin: "10px 0",
    cursor: "pointer",
    fontSize: "1rem",
    fontWeight: "bold",
    display: "block",
    width: "100%",
  },
};

export default TimedQuizPersonalizedScreen;
